import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import CardFoodType, { mapDestinationAddress } from './CardFoodType';
import { ALLOCATION_COLORS, UNASSIGNED_COLOR } from './AllocationBar';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import * as FoodTypesIcons from 'src/components/icons/foodTypes';
import { useMeasureFieldUnits } from './MeasureField';

export const useFoodTypesSelector = ({ oldImages = false } = {}) => {
  const { t } = useTranslation('translation');
  const { user } = useAuth();

  const isAnimalFoodRescuer = !!user.animal_food_rescuer_type;

  const animalFeedType = {
    id: 'animal-feed',
    title: t('data.food-types.animal-feed'),
    image: oldImages
      ? '/static/icons/food-types/ic_animal_feed.svg'
      : '/static/illustrations/food-types/animal-feed.svg',
    icon: FoodTypesIcons.AnimalFeedIcon,
  };

  return [
    ...(isAnimalFoodRescuer ? [animalFeedType] : []),
    {
      id: 'dairy',
      title: t('data.food-types.dairy'),
      image: oldImages
        ? '/static/icons/food-types/ic_dairy.svg'
        : '/static/illustrations/food-types/dairy.svg',
      icon: FoodTypesIcons.DairyIcon,
    },
    {
      id: 'produce',
      title: t('data.food-types.produce'),
      image: oldImages
        ? '/static/icons/food-types/ic_produce.svg'
        : '/static/illustrations/food-types/produce.svg',
      icon: FoodTypesIcons.ProduceIcon,
    },
    {
      id: 'meat',
      title: t('data.food-types.meat'),
      image: oldImages
        ? '/static/icons/food-types/ic_meat.svg'
        : '/static/illustrations/food-types/meat.svg',
      icon: FoodTypesIcons.MeatIcon,
    },
    {
      id: 'prepared',
      title: t('data.food-types.prepared'),
      image: oldImages
        ? '/static/icons/food-types/ic_prepared.svg'
        : '/static/illustrations/food-types/prepared.svg',
      icon: FoodTypesIcons.PreparedIcon,
    },
    {
      id: 'baked',
      title: t('data.food-types.baked'),
      image: oldImages
        ? '/static/icons/food-types/ic_baked.svg'
        : '/static/illustrations/food-types/bakery.svg',
      icon: FoodTypesIcons.BakeryIcon,
    },
    {
      id: 'beverages',
      title: t('data.food-types.beverages'),
      image: oldImages
        ? '/static/icons/food-types/ic_beverages.svg'
        : '/static/illustrations/food-types/beverages.svg',
      icon: FoodTypesIcons.BeveragesIcon,
    },
    {
      id: 'non-perishable',
      title: t('data.food-types.non-perishable'),
      image: oldImages
        ? '/static/icons/food-types/ic_nonperishable.svg'
        : '/static/illustrations/food-types/non-perishable.svg',
      icon: FoodTypesIcons.NonPerishableIcon,
    },
    {
      id: 'non-food',
      title: t('data.food-types.non-food'),
      image: oldImages
        ? '/static/icons/food-types/ic_non_food.svg'
        : '/static/illustrations/food-types/non-food.svg',
      icon: FoodTypesIcons.NonFoodIcon,
    },
    {
      id: 'frozen',
      title: t('data.food-types.frozen'),
      image: oldImages
        ? '/static/icons/food-types/ic_frozen.svg'
        : '/static/illustrations/food-types/frozen.svg',
      icon: FoodTypesIcons.FrozenIcon,
    },
    {
      id: 'compost',
      title: t('data.food-types.compost'),
      image: oldImages
        ? '/static/icons/food-types/ic_compost.png'
        : '/static/illustrations/food-types/compost.svg',
      icon: FoodTypesIcons.CompostIcon,
    },
    ...(isAnimalFoodRescuer ? [] : [animalFeedType]),
  ];
};

export const useGenerateFoodTypesObj = () => {
  const { defaultWeightUnit } = useMeasureFieldUnits();

  return ({
    foodTypeNames,
    isPartialRescue = false,
    existingFoodTypes = {},
  }) => {
    let foodTypes = {};
    foodTypeNames.forEach((foodType) => {
      const parsedFoodType = foodType.replaceAll('_', '-');
      const extraFields = isPartialRescue
        ? {
            minReservableWeight: 0,
            maxReservableWeight: 0,
            maxReservableWeightManuallyChanged: false,
            assignedRecipients: [
              {
                teamId: 'unassigned',
                reservedWeight: 0,
              },
            ],
          }
        : {};

      if (existingFoodTypes[parsedFoodType]) {
        const existing = existingFoodTypes[parsedFoodType];
        const merged = {
          ...extraFields,
          ...existing,
        };
        // Prefill min/max when switching to partial with weight already set
        if (isPartialRescue) {
          const total = parseFloat(merged.value) || 0;
          if (!existing.assignedRecipients?.length && total > 0) {
            merged.assignedRecipients = [
              {
                teamId: 'unassigned',
                reservedWeight: total,
              },
            ];
          }
          if (
            total > 0 &&
            !parseFloat(merged.minReservableWeight) &&
            !parseFloat(merged.maxReservableWeight)
          ) {
            merged.minReservableWeight = 1;
            merged.maxReservableWeight = total;
            merged.maxReservableWeightManuallyChanged = false;
          }
        }
        foodTypes[parsedFoodType] = merged;
      } else {
        foodTypes[parsedFoodType] = {
          value: 0,
          unit: defaultWeightUnit.value,
          ...extraFields,
        };
      }
    });
    return foodTypes;
  };
};

export const handlePartialAddressChange = ({
  teamId,
  newAddress,
  destinationAddresses = [],
  setValue,
}) => {
  const updatedDestinationAddresses = destinationAddresses.map((addr) => {
    if (addr.teamId === teamId) {
      return {
        teamId,
        location: newAddress,
      };
    }
    return addr;
  });
  setValue('destinationAddresses', updatedDestinationAddresses);
};

const DonationFoodTypesSelector = ({
  value,
  onChange,
  isPartialRescue,
  recipients = [],
  errors = {},
  disabledRecipients = [],
  includeFoodTypes = true,
  disabledFoodTypes = [],
  destinationAddresses = [],
  onAddressChange,
  deliveryOption,
  setValue,
}) => {
  const { t } = useTranslation('translation');
  const generateFoodTypesObj = useGenerateFoodTypesObj();
  const foodTypes = useFoodTypesSelector();
  const filteredFoodTypes = foodTypes.filter(
    (foodType) => !!value[foodType.id]
  );

  // Compute stable color map for all assigned recipients
  const colorMap = useMemo(() => {
    const map = new Map();
    const allTeamIds = [];
    Object.values(value || {}).forEach((ft) => {
      (ft.assignedRecipients || []).forEach((r) => {
        if (
          r.teamId &&
          r.teamId !== 'unassigned' &&
          !allTeamIds.includes(r.teamId)
        ) {
          allTeamIds.push(r.teamId);
        }
      });
    });
    allTeamIds.sort();
    allTeamIds.forEach((teamId, idx) => {
      map.set(teamId, ALLOCATION_COLORS[idx % ALLOCATION_COLORS.length]);
    });
    map.set('unassigned', UNASSIGNED_COLOR);
    return map;
  }, [JSON.stringify(value)]);

  useEffect(() => {
    if (isPartialRescue) {
      const currentDestinationAddresses = destinationAddresses || [];
      const uniqueRecipients = Array.from(
        new Set(
          Object.values(value || {})
            .flatMap((ft) => ft.assignedRecipients || [])
            .filter((r) => r.teamId && r.teamId !== 'unassigned')
            .map((r) => r.teamId)
        )
      );

      if (!recipients.length) return;

      const newDestinationAddresses = uniqueRecipients.map((teamId) => {
        const customAddress = currentDestinationAddresses.find(
          (a) => a.teamId === teamId
        );
        const recipient = recipients.find((r) => r.team_id === teamId);
        if (customAddress) {
          return {
            teamId,
            location: customAddress.location,
          };
        } else {
          return {
            teamId,
            location: mapDestinationAddress(recipient),
          };
        }
      });

      setValue('destinationAddresses', newDestinationAddresses);
    }
  }, [
    isPartialRescue,
    JSON.stringify(value),
    JSON.stringify(destinationAddresses),
    JSON.stringify(recipients),
  ]);

  return (
    <Stack spacing={2}>
      {includeFoodTypes && (
        <Stack spacing={1}>
          <Typography variant="h6">{t('donations.food-types')}</Typography>

          <Stack direction="row" gap={1} flexWrap="wrap">
            {foodTypes.map((item) => {
              const selected = !!value[item.id];
              return (
                <Chip
                  onClick={() => {
                    if (selected) {
                      onChange(
                        generateFoodTypesObj({
                          foodTypeNames: Object.keys(value).filter(
                            (foodType) => foodType !== item.id
                          ),
                          isPartialRescue,
                          existingFoodTypes: value,
                        })
                      );
                    } else {
                      onChange(
                        generateFoodTypesObj({
                          foodTypeNames: [...Object.keys(value), item.id],
                          isPartialRescue,
                          existingFoodTypes: value,
                        })
                      );
                    }
                  }}
                  key={item.id}
                  disabled={
                    (Object.keys(value).length === 1 && !!value[item.id]) ||
                    disabledFoodTypes.includes(item.id)
                  }
                  icon={
                    <item.icon
                      sx={{
                        width: 20,
                        height: 20,
                      }}
                    />
                  }
                  sx={{
                    height: 36,
                    borderRadius: 1,
                    px: 0.25,
                    textTransform: 'capitalize',
                    '& .MuiChip-icon': {
                      ml: 1,
                      mr: 0.25,
                    },
                    '& .MuiChip-label': {
                      px: 0.75,
                      fontWeight: selected ? 600 : 500,
                    },
                  }}
                  label={item.title}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'contained' : 'outlined'}
                />
              );
            })}
          </Stack>
        </Stack>
      )}

      {filteredFoodTypes.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="subtitle1">
            {t('donations.food-weights-optional')}
          </Typography>
          {isPartialRescue ? (
            <Typography variant="body2" color="text.secondary">
              {t('donations.partial-food-amounts-helper')}
            </Typography>
          ) : null}
        </Stack>
      )}

      <Box>
        {isPartialRescue ? (
          <Grid container spacing={2}>
            {filteredFoodTypes.map((item) => (
              <Grid key={item.id} size={{ xs: 12, md: 12 }}>
                <CardFoodType
                  disabledRecipients={disabledRecipients}
                  errors={errors}
                  item={item}
                  value={value}
                  onChange={onChange}
                  handleInputChange={(foodTypeId) => (newValue) => {
                    onChange({
                      ...value,
                      [foodTypeId]: newValue,
                    });
                  }}
                  isPartialRescue={isPartialRescue}
                  recipients={recipients}
                  destinationAddresses={destinationAddresses}
                  onAddressChange={onAddressChange}
                  deliveryOption={deliveryOption}
                  colorMap={colorMap}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {filteredFoodTypes.map((item) => (
              <Grid key={item.id} size={{ xs: 12, md: 6 }}>
                <CardFoodType
                  disabledRecipients={disabledRecipients}
                  errors={errors}
                  item={item}
                  value={value}
                  onChange={onChange}
                  handleInputChange={(foodTypeId) => (newValue) => {
                    onChange({
                      ...value,
                      [foodTypeId]: newValue,
                    });
                  }}
                  isPartialRescue={isPartialRescue}
                  recipients={recipients}
                  destinationAddresses={destinationAddresses}
                  onAddressChange={onAddressChange}
                  deliveryOption={deliveryOption}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Stack>
  );
};

export default DonationFoodTypesSelector;
